/**
 * Правила заметок, отделённые от того, кто их рисует.
 *
 * Здесь живёт ровно то, что можно проверить без DOM: когда заметку разрешено сохранить, в
 * каком порядке они идут в панели и как подписать заметку там, где её самой не видно. Хук
 * миниатюр стоит рядом с ними, потому что он о том же предмете и о той же ленте — но его
 * правило («проси кадр, когда карточка впервые попала в поле зрения») без браузера не
 * проверить, и это единственная причина, по которой он не чистая функция.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { FieldMarker, FieldPhoto } from "../api/types";
import { formatFull } from "../lib/format";
import { T } from "../strings";

/**
 * СОЗДАТЬ ПУСТУЮ НЕЛЬЗЯ, СТАТЬ ПУСТОЙ МОЖНО — правило 013, пережившее две модели.
 *
 * Заметка без единого слова законна: её завёл GPS фотографии. Но завести её РУКАМИ, не сказав
 * ни слова и не приложив ни кадра, значит поставить точку, неотличимую от соседней такой же.
 * Поэтому проверка одна и она про содержимое, а не про текст: фотография — такое же
 * содержимое, как и слова.
 *
 * Опустевшая заметка НЕ удаляется автоматически: она видна в панели («без описания») и
 * уходит только руками. Эта функция про создание и только про него.
 */
export const canSaveNote = (text: string, files: number): boolean =>
  text.trim() !== "" || files > 0;

/**
 * Сколько ячеек в ленте — И ЭТО ОДНО ЧИСЛО НА ДВЕ ПОВЕРХНОСТИ.
 *
 * Лента заметки рисуется дважды: в попапе на карте (императивный код на Leaflet) и в карточке
 * панели (React). Разметку они не разделяют и разделять не могут — портал в поддерево,
 * которым владеет Leaflet, это второй владелец одного DOM. Общим обязан быть ЗАКОН, и вот он:
 * ЧЕТЫРЕ ячейки, а пятая фотография превращает последнюю в «+N».
 *
 * Было три (notes-v2), стало четыре (notes-v4): лента переехала из-под раскрытия на саму
 * карточку и стала главным, по чему заметку узнают в списке. При ширине панели 320 px ячейка
 * выходит ≈68 px — миниатюра 300 px покрывает её до DPR 4.
 */
export const STRIP_CELLS = 4;

/**
 * Что показать в ленте и сколько спрятать под «+N».
 *
 * «+N» ведёт в лайтбокс С ТРЕТЬЕЙ фотографии, а не с первой: две видны прямо здесь, и открыть
 * окно на том, что уже показано, значит потратить клик на пролистывание назад. Индекс первой
 * спрятанной и есть длина показанного.
 */
export const stripCells = <T>(photos: readonly T[]): { shown: T[]; rest: number } => {
  if (photos.length <= STRIP_CELLS) return { shown: [...photos], rest: 0 };
  const shown = photos.slice(0, STRIP_CELLS - 1);
  return { shown, rest: photos.length - shown.length };
};

/**
 * Порядок панели: свежая поездка сверху.
 *
 * `happened_on` УБЫВ., тай-брейк `id` УБЫВ. — и оба ключа обязательны. Дата тут человеческая:
 * в один день попадает вся поездка, и без второго ключа порядок внутри дня отдан на волю
 * того, как сервер сложил строки, — список молча перетасовывался бы между двумя открытиями
 * вкладки. `id` различает их всегда и, в отличие от `created_at`, не врёт при переносе: у
 * заметки, заведённой сервером из старого снимка, дата давняя, а строка новая.
 *
 * Копия, а не сортировка на месте: массив приходит из состояния, и мутировать его значит
 * менять то, по чему React сравнивает рендеры.
 */
export const sortNotes = (notes: readonly FieldMarker[]): FieldMarker[] =>
  [...notes].sort((a, b) => {
    if (a.happened_on !== b.happened_on) return a.happened_on < b.happened_on ? 1 : -1;
    return b.id - a.id;
  });

/** Есть ли у заметки место. Половины координаты не бывает — проверяются обе. */
export const hasPlace = (note: FieldMarker): boolean => note.lon !== null && note.lat !== null;

/** Фотографии ОДНОЙ заметки, в порядке показа: свежий день первым, внутри дня — свежая запись. */
export const photosOfNote = (photos: readonly FieldPhoto[], noteId: number): FieldPhoto[] =>
  photos
    .filter((photo) => photo.marker_id === noteId)
    .sort((a, b) => {
      if (a.taken_on !== b.taken_on) return a.taken_on < b.taken_on ? 1 : -1;
      return b.id - a.id;
    });

/** Сколько текста заметки влезает в подпись, за которой стоит что-то ещё. */
const LABEL_CHARS = 40;

/**
 * Имя заметки там, где её самой не видно: свёрнутая строка панели, подпись в списке переноса,
 * середина доступного имени флажка.
 *
 * ДАТА — `happened_on`, и это тот самый арбитраж: одна заметка, датированная в попапе одним
 * днём, а в списке переноса другим, — это две заметки для всякого, кто читает оба списка.
 * Заметка без слов зовётся `markers.noText`, а не «пустой»: в ней есть фотография, нет
 * описания.
 */
export const noteLabel = (note: FieldMarker, hasPhotos = false): string => {
  const text = note.note.trim();
  /**
   * «БЕЗ ОПИСАНИЯ» ТОЛЬКО ТАМ, ГДЕ И ПРАВДА НИЧЕГО НЕТ.
   *
   * Заголовок заметки, у которой нет слов, но есть кадры, — это одна дата: содержимое у неё
   * есть, и оно видно ячейкой ленты прямо под заголовком. Приписка «без описания» над живой
   * лентой описывает не заметку, а нашу модель — человек читает её как «здесь пусто» и идёт
   * проверять, куда делись фотографии.
   *
   * Слова остаются ровно для случая «ни текста, ни кадров»: там назвать заметку больше нечем,
   * а пустая строка на месте описания — это не «пусто», это сломанная карточка.
   */
  if (text === "") {
    if (hasPhotos) return formatFull(note.happened_on);
    // Дата уже стоит первой половиной строки — второй раз её сюда подставлять нельзя, поэтому
    // безымянная заметка зовётся здесь `noText`, а не датой, как в `noteName`.
    return T.markers.peek(formatFull(note.happened_on), T.markers.noText);
  }
  const head = text.length > LABEL_CHARS ? `${text.slice(0, LABEL_CHARS)}…` : text;
  return T.markers.peek(formatFull(note.happened_on), head);
};

/**
 * КОРОТКОЕ имя заметки — для строки, в которой она не одна.
 *
 * Отличается от `noteLabel` тем, что НЕ несёт дату: лист тулбара в режиме постановки ставит
 * это имя внутрь собственной фразы, за которой идёт ещё два предложения («Дальше 500 м…»,
 * «Esc — отмена»). Полный текст заметки уводил хвост фразы под пилюлю даты и под консоль, то
 * есть фраза переставала дочитываться ровно там, где написано, как из режима выйти.
 *
 * Дата подставляется только тогда, когда текста нет вовсе: назвать заметку надо хоть как-то, а
 * «без описания» в кавычках посреди фразы читается как имя, которого человек не давал.
 */
export const noteName = (note: FieldMarker): string => {
  const text = note.note.trim();
  if (text === "") return formatFull(note.happened_on);
  return text.length > LABEL_CHARS ? `${text.slice(0, LABEL_CHARS)}…` : text;
};

/**
 * Насколько заранее просить миниатюры карточки, которая ещё не доехала до экрана.
 *
 * 200 px — примерно одна свёрнутая карточка: кадр успевает приехать за то время, пока палец
 * доводит прокрутку, и при этом список из двадцати заметок не тянет шестьдесят миниатюр на
 * открытие вкладки.
 */
export const THUMB_ROOT_MARGIN = "200px";

export interface NoteThumbs {
  /** Готовые blob-URL по id фотографии. */
  urls: Map<number, string>;
  /**
   * Ref-обработчик для корня карточки: по нему карточку и начинают наблюдать. `null` (React
   * зовёт так при размонтировании) снимает наблюдение.
   */
  watch: (noteId: number, photos: FieldPhoto[]) => (node: HTMLElement | null) => void;
}

/**
 * Миниатюры лент — ПО МЕРЕ ПОЯВЛЕНИЯ КАРТОЧКИ В ПОЛЕ ЗРЕНИЯ.
 *
 * Двадцать заметок по три кадра — это шестьдесят запросов и шестьдесят декодированных
 * картинок в памяти на каждое открытие вкладки, из которых видно три. `IntersectionObserver`
 * просит их ровно тогда, когда карточка впервые пересекла видимую часть, и ровно по разу:
 * `asked` помнит спрошенное, `failed` — не приехавшее, и повторно не просит ни то ни другое.
 *
 * ОТЗЫВ ОДИН, НА РАЗМОНТИРОВАНИИ. Каждый blob-URL держит декодированную картинку, пока его не
 * отозвали; карта живёт на ссылке, поэтому переключение вкладки её не теряет, а уход панели
 * отзывает всё разом. Наблюдатель создаётся один на хук — двадцать наблюдателей по карточке
 * стоили бы столько же, сколько сами миниатюры.
 */
export const useNoteThumbs = (
  load: ((photoId: number) => Promise<string>) | undefined,
  /**
   * Чьи это миниатюры. Смена поля ОТЗЫВАЕТ всё накопленное: карта блобов жила на рефе и
   * очищалась только при размонтировании панели, а панель переживает выбор любого числа
   * полей — по три декодированных картинки на заметку за каждое, до конца жизни вкладки.
   */
  fieldId: number | null = null,
): NoteThumbs => {
  const urls = useRef(new Map<number, string>());
  const asked = useRef(new Set<number>());
  const failed = useRef(new Set<number>());
  /**
   * Панель снята (или поле сменилось). Кадры, приехавшие ПОСЛЕ этого, отзываются на месте —
   * класть их некуда: карта уже очищена, и второго отзыва на них не будет никогда. Тот же
   * приём и по той же причине, что `detached` в слое флажков (map/flags.ts).
   */
  const detached = useRef(false);
  /** Что показывает карточка, пока её наблюдают: узел → кадры. */
  const watched = useRef(new Map<Element, FieldPhoto[]>());
  /** Последний узел каждой карточки: по нему снимается наблюдение, когда карточка ушла. */
  const nodes = useRef(new Map<number, Element>());
  const observer = useRef<IntersectionObserver | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const [, bump] = useState(0);

  useEffect(() => {
    const held = urls.current;
    return () => {
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  const fetchThumbs = useCallback((photos: FieldPhoto[]): void => {
    const get = loadRef.current;
    if (!get) return;
    for (const photo of photos) {
      if (asked.current.has(photo.id) || failed.current.has(photo.id)) continue;
      asked.current.add(photo.id);
      void get(photo.id).then(
        (url) => {
          if (detached.current) {
            URL.revokeObjectURL(url);
            return;
          }
          urls.current.set(photo.id, url);
          bump((token) => token + 1);
        },
        () => {
          // Одна миниатюра не пришла — ячейка остаётся пустой рамкой. Ноты об этом нет: дата
          // и текст на месте, кадр открывается, и сообщать не о чем.
          //
          // Неудача помнится, чтобы не долбить сервер в цикле, но НЕ НАВСЕГДА: `asked`
          // снимается вместе с ней, и следующее появление карточки в поле зрения — это новая
          // попытка. Иначе одна потерянная секунда связи оставляла ячейку пустой до смены
          // поля, хотя прокрутить список туда-обратно стоит ничего.
          failed.current.add(photo.id);
          asked.current.delete(photo.id);
          bump((token) => token + 1);
        },
      );
    }
  }, []);

  /**
   * Наблюдатель создаётся лениво и ровно один. `IntersectionObserver` может не существовать
   * вовсе (jsdom, старые движки) — тогда правило вырождается в «проси сразу»: показать
   * миниатюры важнее, чем сэкономить на них в среде, где экрана всё равно нет.
   */
  const ensure = useCallback((): IntersectionObserver | null => {
    if (observer.current) return observer.current;
    if (typeof IntersectionObserver !== "function") return null;
    observer.current = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (!record.isIntersecting) continue;
          const photos = watched.current.get(record.target);
          if (photos) {
            // Карточка снова на экране — прошлые неудачи забываются: это вторая попытка, а не
            // повторение того же запроса.
            for (const photo of photos) failed.current.delete(photo.id);
            fetchThumbs(photos);
          }
          // ПО РАЗУ НА КАРТОЧКУ: дальше она может уезжать и возвращаться сколько угодно, а
          // кадры уже спрошены. Наблюдение снимается здесь же, чтобы прокрутка туда-обратно
          // не стоила ни одного лишнего колбэка.
          observer.current?.unobserve(record.target);
        }
      },
      { rootMargin: THUMB_ROOT_MARGIN },
    );
    return observer.current;
  }, [fetchThumbs]);

  useEffect(() => {
    detached.current = false;
    // Карты сняты в переменные эффекта, а не прочитаны из `ref` в уборщике: к моменту
    // уборки `ref` может указывать уже на другую карту, и очищена была бы не та.
    const seen = watched.current;
    const held = nodes.current;
    return () => {
      detached.current = true;
      observer.current?.disconnect();
      observer.current = null;
      seen.clear();
      held.clear();
    };
  }, []);

  /**
   * СМЕНА ПОЛЯ ОТЗЫВАЕТ ВСЁ. Соседнее поле — другие заметки и другие кадры; держать чужие
   * блобы значит платить памятью за каждое поле, которое человек открыл за сессию. Забывается
   * и `failed`: неудача принадлежала прежнему полю, и новому она ничего не обещает.
   */
  useEffect(() => {
    if (fieldId === null) return;
    // Карты и множества сняты в переменные эффекта: к моменту уборки рефы уже могут указывать
    // на другие, и очищены были бы не те.
    const held = urls.current;
    const seenIds = asked.current;
    const lost = failed.current;
    const seen = watched.current;
    const known = nodes.current;
    return () => {
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
      seenIds.clear();
      lost.clear();
      observer.current?.disconnect();
      observer.current = null;
      seen.clear();
      known.clear();
    };
  }, [fieldId]);

  const watch = useCallback(
    (noteId: number, photos: FieldPhoto[]) =>
      (node: HTMLElement | null): void => {
        if (node === null) {
          // Карточка размонтирована (свернули, сменили поле, ушли на «Анализ»). Без этого
          // наблюдатель держал бы ссылку на снятый узел до конца жизни панели.
          const gone = nodes.current.get(noteId);
          if (gone) {
            observer.current?.unobserve(gone);
            watched.current.delete(gone);
            nodes.current.delete(noteId);
          }
          return;
        }
        nodes.current.set(noteId, node);
        watched.current.set(node, photos);
        const active = ensure();
        if (active === null) {
          fetchThumbs(photos);
          return;
        }
        active.observe(node);
      },
    [ensure, fetchThumbs],
  );

  return { urls: urls.current, watch };
};
